import type { ChangeEvent, ChangeEventHandler, FC, HTMLProps } from 'react';
import { memo, useCallback, useRef } from 'react';
import { IMaskInput } from 'react-imask';
import { notifications } from '@utils/notifications';

interface InputMaskProps extends Omit<HTMLProps<HTMLInputElement>, 'onChange'> {
  /** Маска в старом формате react-input-mask (9 = цифра). Конвертируется в формат IMask автоматически. */
  mask: string;
  /** Совместимость со старым API. '' (по умолчанию в проекте) => без символов-заполнителей (lazy). */
  maskChar?: string;
  type?: 'tel' | string;
  disabled?: boolean;
  value: string;
  className?: string;
  placeholder?: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  inputRef?: any;
  name?: string;
}

const normalizeToLocalPhone = (
  rawValue: string,
  opts?: { isIosAutofill?: boolean }
): string | null => {
  const digits = rawValue.replace(/\D/g, '');

  if (digits.length === 10 && digits[0] === '9') {
    return digits;
  }

  if (
    digits.length === 11 &&
    (digits[0] === '7' || digits[0] === '8') &&
    digits[1] === '9'
  ) {
    return digits.slice(1);
  }

  if (opts?.isIosAutofill && digits.length === 9) {
    const restored = `9${digits}`;
    if (restored.length === 10) {
      return restored;
    }
  }

  return null;
};

/**
 * react-input-mask использует `9` как плейсхолдер цифры, а IMask — `0`.
 * Литералы (+, 7, скобки, пробелы, дефисы) остаются как есть.
 */
const toIMaskPattern = (mask: string): string => mask.replace(/9/g, '0');

const InputMask: FC<InputMaskProps> = props => {
  const { mask, maskChar, value, onChange, inputRef, ...rest } = props;

  const imaskPattern = toIMaskPattern(mask);
  const isLazy = !maskChar; // maskChar='' => lazy (без заполнителей), как раньше

  const prepare = useCallback(
    (appended: string, masked: { unmaskedValue: string }): string => {
      const appendedDigits = appended.replace(/\D/g, '');
      const currentDigits = (masked?.unmaskedValue ?? '').replace(/\D/g, '');

      if (currentDigits.length === 0 && appendedDigits.length === 1) {
        if (appendedDigits === '7' || appendedDigits === '8') {
          notifications(
            'Номер телефона нужно вводить без начальной 7 и 8',
            'Внимание'
          );
          return '';
        }
      }

      if (currentDigits.length === 0 && appendedDigits.length >= 9) {
        const local = normalizeToLocalPhone(appended, { isIosAutofill: true });
        if (local) {
          return local;
        }
      }

      return appended;
    },
    []
  );

  // Последнее значение, которое выдала сама маска (masked-строка, например
  // "+7 (999) 123-45-67"). Нужно, чтобы отличать ввод пользователя от внешнего
  // изменения value.
  const lastAcceptedRef = useRef<string>('');

  const handleAccept = useCallback(
    (nextValue: string) => {
      lastAcceptedRef.current = nextValue;
      const event = {
        target: { value: nextValue, name: props.name ?? '' },
        currentTarget: { value: nextValue, name: props.name ?? '' }
      } as unknown as ChangeEvent<HTMLInputElement>;

      onChange?.(event);
    },
    [onChange, props.name]
  );

  // react-imask на каждый ре-рендер выполняет `maskRef.value = props.value`.
  // Сеттер делает no-op только при ПОЛНОМ совпадении строк (`if (value === str)`),
  // иначе переустанавливает значение и сбрасывает каретку (alignCursor) — из-за
  // чего ломается ввод и удаление по Backspace, когда потребитель (OldInput)
  // хранит «голые» цифры, а маска внутри — отформатированную строку.
  // Поэтому: если внешнее value содержит те же цифры, что мы только что отдали
  // через onAccept, возвращаем маске её же строку — тогда сеттер делает no-op и
  // каретка не дёргается. Любое реальное внешнее изменение (очистка, префилл,
  // вставка) меняет цифры и проходит в маску как обычно.
  const onlyDigits = (str: unknown): string =>
    String(str ?? '').replace(/\D/g, '');
  const valueForMask =
    onlyDigits(value) === onlyDigits(lastAcceptedRef.current)
      ? lastAcceptedRef.current
      : value ?? '';

  return (
    <IMaskInput
      {...(rest as any)}
      mask={imaskPattern}
      lazy={isLazy}
      placeholderChar={maskChar && maskChar.length > 0 ? maskChar : undefined}
      unmask={false}
      value={valueForMask}
      inputRef={inputRef}
      prepare={prepare as any}
      onAccept={handleAccept}
    />
  );
};

export default memo(InputMask);
